import { formatSize } from '@provision/rich-text-editor';
import { getFileTypeFromContentType } from 'src/components/AddressFilesDropzone/utils';
import { config } from 'src/core/config';
import type { ConversationAttachment } from 'src/graphql/entities/conversation/conversation.types';
import type { MoveAttachmentToSiteFilesAttachment } from 'src/graphql/entities/files-upload.types';
import { getMoveAttachmentToSiteFilesPayload } from 'src/graphql/entities/files-upload.utils';

export type AttachmentIconName = 'description' | 'image' | 'insert_drive_file';

export interface AttachmentRowModel {
  fileExtension?: string;
  id: string;
  siteFilePayload: MoveAttachmentToSiteFilesAttachment;
  displayDate: string;
  displaySize?: string;
  fileNameBase: string;
  fileName: string;
  iconName: AttachmentIconName;
  url?: string;
}

const splitAttachmentFileName = (fileName: string) => {
  const extensionSeparatorIndex = fileName.lastIndexOf('.');

  if (extensionSeparatorIndex <= 0 || extensionSeparatorIndex === fileName.length - 1) {
    return {
      fileExtension: undefined,
      fileNameBase: fileName,
    };
  }

  return {
    fileExtension: fileName.slice(extensionSeparatorIndex),
    fileNameBase: fileName.slice(0, extensionSeparatorIndex),
  };
};

const attachmentDateFormatter = Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export const getConversationAttachmentUrl = (
  attachment: Pick<ConversationAttachment, 'conversationId' | 'fileKey' | 'partnerId'>,
): string | undefined => {
  if (!config.s3AttachmentsUrl || !attachment.fileKey) {
    return undefined;
  }

  return `${config.s3AttachmentsUrl}${attachment.partnerId}/${attachment.conversationId}/${attachment.fileKey}`;
};

export const getAttachmentIconName = (contentType?: string | null): AttachmentIconName => {
  const fileType = getFileTypeFromContentType(contentType ?? '');

  if (fileType === 'img') {
    return 'image';
  }

  if (['doc', 'pdf', 'txt'].includes(fileType)) {
    return 'description';
  }

  return 'insert_drive_file';
};

export const mapConversationAttachmentRows = (
  attachments: ConversationAttachment[],
): AttachmentRowModel[] => {
  return attachments
    .filter(
      (a) =>
        typeof a.conversationAttachmentId === 'string' &&
        typeof a.fileName === 'string' &&
        typeof a.createdAt === 'string' &&
        !Number.isNaN(new Date(a.createdAt).getTime()),
    )
    .toSorted(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )
    .map((attachment) => ({
      id: attachment.conversationAttachmentId,
      siteFilePayload: getMoveAttachmentToSiteFilesPayload(attachment),
      displayDate: attachmentDateFormatter.format(new Date(attachment.createdAt)),
      displaySize:
        typeof attachment.size === 'number' ? formatSize(attachment.size) : undefined,
      ...splitAttachmentFileName(attachment.fileName),
      fileName: attachment.fileName,
      iconName: getAttachmentIconName(attachment.contentType),
      url: getConversationAttachmentUrl(attachment),
    }));
};
